import React from 'react';
import LegalPage, { Section, List, Link } from '@/components/Pages/LegalPage';
import Paragraph from '@/components/Typography/Paragraph';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';

const PrivacyPolicyPage = () => {
  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="September 15, 2026"
    >
      <MetaHead 
        title="Privacy Policy"
        description="How Gab AI handles conversations, Work files and browser sessions, connected services, payment information, and your privacy choices."
        canonical="/privacy-policy"
        keywords="Gab AI privacy, AI privacy policy, Work agent data, connected services, data protection, credit payments"
      />

      <Section title="Privacy That Actually Means Something">
        <Paragraph>
          At Gab AI, privacy isn't a marketing slogan — it's a core principle. While Big Tech platforms harvest your data to sell ads and manipulate your behavior, we're building something different: an AI platform where your conversations stay yours.
        </Paragraph>
        <Paragraph>
          This Privacy Policy explains how we handle your information when you use Gab AI. We aim for complete transparency — no fine print, no surprises. If something isn't clear, reach out. We're real people, and we'll give you a straight answer.
        </Paragraph>
        <Paragraph>
          This policy should be read alongside our <Link href="/terms-of-service">Terms of Service</Link> and <Link href="/terms-of-sale">Terms of Sale</Link>.
        </Paragraph>
      </Section>

      <Section title="Who We Are">
        <Paragraph>
          Gab AI is a US-based company located in Pennsylvania. We're a small, independent team building AI tools outside the control of Big Tech. Our mission is simple: make powerful AI accessible to everyone while respecting your privacy and freedom.
        </Paragraph>
        <Paragraph>
          We've developed "Arya," our own conversational AI, and we integrate various third-party AI models to give you access to the best tools available. But here's what matters most: we're not beholden to venture capitalists, advertising networks, or data brokers. We answer to you — our users.
        </Paragraph>
      </Section>

      <Section title="AI Providers and Identifying Information">
        <Paragraph>
          Requests to integrated AI providers are made through Gab AI's provider accounts. This does not make the
          information in a request anonymous. Prompts, uploaded files, images, Work instructions, browser pages,
          and tool results can contain names, contact details, confidential information, or other information that
          identifies you or another person.
        </Paragraph>
        <Paragraph>
          Relevant content may be sent to the models and tools used for your request. A website or connected
          service may also recognize you when you log into your account or direct Work to interact with it.
          Consider the information a task requires before granting access or supplying materials, and provide only
          information you are authorized to share. The sections below explain these data flows and the roles of
          service providers and independent third parties.
        </Paragraph>
      </Section>

      <Section title="Information We Collect">
        <Paragraph>
          We collect only what's necessary to provide and improve our service. Here's what we gather and why:
        </Paragraph>

        <Section title="Account Information">
          <Paragraph>
            When you create an account, we collect basic information: your name, email address, and account credentials. If you're logging in through a third-party service (if we offer that option), they'll share your info with us at your direction.
          </Paragraph>
          <Paragraph>
            <Text weight='medium'>Why we collect it:</Text> To create your account, verify you're 18 or older, provide support, keep your account secure, and communicate important service updates.
          </Paragraph>
        </Section>

        <Section title="Payment Information">
          <Paragraph>
            If you subscribe or purchase credits, payment processors handle your payment credentials. We receive and store the payment references and transaction information needed to administer purchases, such as provider identifiers, limited payment-method details, amounts, credit allocations, payment status, and receipts.
          </Paragraph>
          <Paragraph>
            <Text weight='medium'>Why we collect it:</Text> To process subscriptions, one-time credit purchases, and Auto Top-Up; reconcile payments; handle billing errors; and maintain required transaction records. For Auto Top-Up, we record the settings and pack price you authorize, the selected payment-method reference, the time of consent, and the applicable authorization version. We use these records to enforce your purchase limits and administer future charges you have authorized.
          </Paragraph>
        </Section>

        <Section title="Your AI Conversations">
          <Paragraph>
            When you interact with our AI models, we see your prompts (Input) and the AI's responses (Output). Together, we call this "User Content."
          </Paragraph>
          <Paragraph>
            <Text weight='extraBold'>Important:</Text> Avoid unnecessary sensitive information in your prompts and files. Content supplied to the Service may be included in requests to AI providers and tools and may appear in the output. Do not supply information you lack permission to share.
          </Paragraph>
          <Paragraph>
            <Text weight='medium'>Why we collect it:</Text> To generate responses, improve our models, provide support if something goes wrong, maintain security, and develop new features.
          </Paragraph>
        </Section>

        <Section title="Technical Information">
          <Paragraph>
            We collect standard technical data: IP address, device type, browser, operating system, general location (country/region from your IP), and how you use the service (which features you use, conversation history, interaction patterns).
          </Paragraph>
          <Paragraph>
            We also use cookies and similar technologies, including those set by third-party analytics and advertising platforms (such as Meta/Facebook and Reddit), to help us understand how visitors reach our site and to measure the effectiveness of our advertising. These cookies may collect identifiers such as your IP address, browser information, and interaction data.
          </Paragraph>
          <Paragraph>
            <Text weight='medium'>Why we collect it:</Text> To keep the service running smoothly, fix bugs, improve performance, prevent abuse, understand how people use Gab AI, and measure the effectiveness of our advertising efforts.
          </Paragraph>
        </Section>

        <Section title="Communications">
          <Paragraph>
            If you email us, use our support chat, or send feedback, we keep those messages.
          </Paragraph>
          <Paragraph>
            <Text weight='medium'>Why we collect it:</Text> To respond to your questions, fix problems, and improve the service based on your feedback.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Work Tasks, Files, Memory, and Browser Sessions">
        <Paragraph>
          When you use Work, we process your task instructions, attachments, generated and modified files, task
          history, tool inputs and results, progress and error records, and the permissions or approvals you
          provide. Depending on the tools used, this can include browser page content, screenshots, downloaded
          materials, and information from connected accounts. We use this information to carry out tasks, deliver
          results, meter usage, maintain security, investigate failures, and provide support.
        </Paragraph>
        <Paragraph>
          Where memory or project context is enabled, saved information may be used in later tasks. Work files,
          task history, and saved memory can be maintained separately. Removing one item does not necessarily
          remove copies already saved in another file, task, or memory. Use the available controls for the relevant
          information, or contact us for assistance.
        </Paragraph>
        <Paragraph>
          Work browser sessions may preserve cookies and login state across tasks so that you can continue using
          websites you have signed into. Browser infrastructure providers may process session data on our behalf.
          Logging into a website identifies you to that website according to its own practices. Closing a task or
          browser viewer does not necessarily sign you out of a persistent session. Use available sign-out or
          account-access controls and contact support if you need help removing a connection.
        </Paragraph>
        <Paragraph>
          Relevant Work materials may be processed by our hosting, storage, browser, model, and tool providers.
          When you direct Work to send, publish, or share materials, the selected recipients or services receive
          those materials; public sharing can make them accessible to others. This operational processing does not
          by itself authorize us to publish your private Work materials for unrelated purposes. Model and product
          improvement uses remain subject to this policy, applicable choices, and any consent required by law.
        </Paragraph>
      </Section>

      <Section title="Gab AI Browser Extension">
        <Paragraph>
          Side chat uses your Gab AI account and conversations without requiring the Mac app. Sign-in
          information stays with gab.ai; it is not copied into extension storage. When you choose Add page
          to chat or Ask Gab AI, the selected page title, address, and text are added to a draft. Review the
          draft before sending. Sending shares that content with Gab AI and the model and service providers
          used for your conversation, and it can remain in conversation history. The extension keeps the
          current conversation reference for each tab during the browser session. Pending selected text
          is held temporarily until it is added or expires.
        </Paragraph>
        <Paragraph>
          Browser automation requires a connected Mac app and task access. Tab discovery can share titles
          and addresses of open ordinary browser tabs so the task can choose a tab. After website approval,
          a task can read page text, visible form content, and screenshots and interact with the page.
          These materials can contain personal information and are sent through the Mac app to Gab AI and
          its task providers; relevant results can remain in task history. Password-field protections do
          not remove all sensitive information visible on a page or in a screenshot. Incognito access is
          disabled. Use Take over or Stop to control automation, and manage saved website approvals in the
          Mac app. Manage page-sharing permissions in your browser's extension settings.
        </Paragraph>
      </Section>

      <Section title="macOS App and Computer Access">
        <Paragraph>
          When you enable local folder, command, browser, or app access, the Gab AI macOS app can retrieve
          information for your tasks within the applicable permissions. This can include file contents and names,
          folder paths, application or browser content, screenshots of permitted windows, command inputs and
          results, and records of permissions and actions. Relevant information is sent to Gab AI's cloud services
          and may be processed by the hosting, model, and tool providers used for the task. “Local” access does
          not mean that all processing stays on your Mac. Accessed materials may contain personal or confidential
          information even if you did not manually attach a file or include that information in a prompt.
        </Paragraph>
        <Paragraph>
          Manage folder and browser or app access through the available app settings, and manage operating-system
          permissions through macOS settings. Other grants for the same resource may require separate revocation.
          Stopping access limits future use of that access; it does not undo completed actions or automatically
          delete task history, previously transmitted content, saved files, or browser login state. The Work,
          service-provider, sharing, retention, and deletion provisions of this policy also apply to information
          processed through these features. Information you direct the app to send to independent websites or
          recipients is also subject to those parties' practices.
        </Paragraph>
      </Section>

      <Section title="How We Use Your Information">
        <Paragraph>
          We use your information to:
        </Paragraph>
        <List items={[
          'Provide the service: Process your AI requests, manage your account, handle payments',
          'Improve our platform: Develop new features, fix bugs, understand usage patterns',
          'Support you: Answer questions, troubleshoot problems, provide assistance',
          'Keep things secure: Prevent fraud, abuse, and unauthorized access',
          'Comply with the law: Meet legal obligations and respond to lawful requests',
          'Communicate important updates: Service changes, security alerts, policy updates (not marketing spam)',
          'Measure advertising effectiveness: Understand how users find us and whether our ads lead to signups or purchases (see "Advertising and Conversion Measurement" below)'
        ]} />
      </Section>

      <Section title="Who We Share Your Information With">
        <Paragraph>
          <Text weight='extraBold'>We're not in the business of selling your data.</Text> Here's who we share with and why:
        </Paragraph>

        <Section title="Service Providers">
          <Paragraph>
            We work with companies that provide hosting, cloud infrastructure, payment processing, and other technical services. These providers are bound by confidentiality agreements and can only use your data to help us run the service.
          </Paragraph>
        </Section>

        <Section title="Third-Party AI Models">
          <Paragraph>
            When a request uses an integrated AI model, we send the relevant content through the provider's API. For Work, this can include task instructions, attachments, prior task context, browser content, and tool results needed for processing. That content may itself identify you or another person.
          </Paragraph>
          <Paragraph>
            Processing by AI providers is subject to our applicable arrangements with them and their applicable terms and policies. Do not assume that using a model through Gab AI removes identifying information from the content you provide.
          </Paragraph>
        </Section>

        <Section title="Connected Websites and Independent Services">
          <Paragraph>
            When you authorize a task involving an independent website, application, or recipient, relevant
            information is transmitted to that destination. Its own terms and privacy practices govern its handling
            of information it receives. Disconnecting access does not recall previously transmitted information or
            delete copies held by that destination. You may need to use its own controls or contact it to request
            deletion. Our obligations concerning our own processing and providers acting on our behalf remain
            applicable.
          </Paragraph>
        </Section>

        <Section title="Advertising Measurement Partners">
          <Paragraph>
            We use advertising platforms, including Meta (Facebook) and Reddit, to promote Gab AI. To measure whether our ads are effective, we share certain information with these platforms using industry-standard tools such as tracking pixels and server-side conversion APIs.
          </Paragraph>
          <Paragraph>
            The information shared may include hashed (cryptographically obscured) versions of your email address and account identifier, along with your IP address, browser information, and details about actions you take on our site (such as signing up or making a purchase). This data is used solely to measure ad performance and is not used to build advertising profiles about you for other companies' benefit.
          </Paragraph>
          <Paragraph>
            <Text weight='extraBold'>Important:</Text> This sharing is limited to conversion measurement — determining whether an ad led to a signup or purchase. We do not sell your personal information, and we do not allow advertising partners to use your data for purposes unrelated to measuring our own ad campaigns. Your AI conversations are never shared with advertising partners.
          </Paragraph>
        </Section>

        <Section title="Legal Requirements">
          <Paragraph>
            We'll disclose information if required by law, court order, or legal process. We'll also share data if necessary to protect our rights, your safety, or the safety of others.
          </Paragraph>
        </Section>

        <Section title="Business Transfers">
          <Paragraph>
            If Gab AI is acquired or merged with another company, your information may be transferred as part of that transaction. We'll notify you if this happens and if there are any changes to how your data is handled.
          </Paragraph>
        </Section>

        <Section title="Gab Family of Services">
          <Paragraph>
            We may share information with our affiliated companies (like Gab.com) for operational, technical, or support purposes consistent with this policy.
          </Paragraph>
        </Section>
      </Section>

      <Section title="How Long We Keep Your Information">
        <Paragraph>
          We keep your information as long as necessary to provide the service, comply with legal obligations, and protect our legitimate interests.
        </Paragraph>
        <Paragraph>
          If you delete conversations or close your account, we generally remove the data from active systems within 30 days — unless we're required to keep it for legal, safety, or security reasons. Backup systems may retain data longer before being overwritten. Work materials are subject to these retention principles; separately saved files, task history, memory, and browser sessions may require separate removal through available controls or support. We retain payment, Auto Top-Up authorization, and transaction records as needed for accounting, fraud prevention, billing disputes, and legal obligations. Deleting a task or disabling Auto Top-Up does not by itself delete those records or information already sent to an independent third party.
        </Paragraph>
      </Section>

      <Section title="Advertising and Conversion Measurement">
        <Paragraph>
          <Text weight='extraBold'>We don't sell your data.</Text> We don't share your personal information with third parties so they can market their products to you. We don't let advertisers target you based on your AI conversations or browsing behavior on our platform.
        </Paragraph>
        <Paragraph>
          We do use conversion measurement tools (such as the Meta Pixel, Meta Conversions API, Reddit Pixel, and Reddit Conversions API) to understand whether our own advertising campaigns are working — for example, whether an ad we placed led someone to sign up or subscribe. This involves sharing limited, hashed data (like a cryptographic hash of your email) with the advertising platform so it can confirm a conversion occurred. This is a standard practice used to measure ad effectiveness, not to build a profile about you or target you with third-party ads.
        </Paragraph>
        <Paragraph>
          If we need to send you something, it's because it's directly related to the service you're using — like a security alert or important policy change.
        </Paragraph>
      </Section>

      <Section title="Security">
        <Paragraph>
          We implement industry-standard security measures to protect your information. But let's be honest: no system is 100% secure. We do our best, and we're constantly improving our defenses.
        </Paragraph>
        <Paragraph>
          You play a role too: keep your password secure, don't share your account credentials, and be careful about what information you include in your AI prompts.
        </Paragraph>
      </Section>

      <Section title="Children's Privacy">
        <Paragraph>
          <Text weight='extraBold'>Gab AI is not intended for anyone under 18 years old.</Text> We don't knowingly collect information from minors. If you're under 18, please don't use our service.
        </Paragraph>
        <Paragraph>
          If we discover we've inadvertently collected information from someone under 18, we'll delete it immediately. If you believe this has happened, contact us right away at legal@gab.ai.
        </Paragraph>
      </Section>

      <Section title="Links to Other Sites">
        <Paragraph>
          Our service may link to external websites we don't control. Those sites have their own privacy policies. We're not responsible for how they handle your information.
        </Paragraph>
      </Section>

      <Section title="Changes to This Policy">
        <Paragraph>
          We may update this Privacy Policy from time to time. We will update the "Last Updated" date, identify when material changes take effect, and provide any additional or advance notice required by law.
        </Paragraph>
        <Paragraph>
          Where a change requires consent, including certain materially different uses of information already collected, we will obtain that consent before applying the change. Continued use or acceptance of general terms does not replace consent required by applicable law.
        </Paragraph>
      </Section>

      <Section title="The Bottom Line">
        <Paragraph>
          Privacy shouldn't be a luxury. It shouldn't require a law degree to understand. And it definitely shouldn't be an afterthought.
        </Paragraph>
        <Paragraph>
          At Gab AI, we're building a platform where you can use powerful AI tools without sacrificing your privacy to Big Tech. Consider what your prompts, files, and connected accounts disclose when choosing the tools to use. Your data isn't for sale. And you're treated like a person, not a product.
        </Paragraph>
        <Paragraph>
          <Text weight='extraBold'>That's our commitment to you.</Text>
        </Paragraph>
      </Section>

      <Section title="Contact Us">
        <Paragraph>
          If you have questions about this Privacy Policy or how we handle your information, we want to hear from you.
        </Paragraph>
        <Paragraph>
          <Text weight='medium'>Gab AI Inc.</Text>{'\n'}
          1143 Northern Blvd #158{'\n'}
          Clarks Summit, PA 18411{'\n'}
          United States of America
        </Paragraph>
        <Paragraph>
          Email: legal@gab.ai{'\n'}
          Support: ai@gab.com
        </Paragraph>
      </Section>
    </LegalPage>
  );
};

export default React.memo(PrivacyPolicyPage);
